import React from 'react';
import styled from 'styled-components';
import tw from 'twin.macro';
import errorMonitoring from '../utils/errorMonitoring';

const ErrorContainer = styled.div`
    ${tw`min-h-screen flex items-center justify-center bg-gray-500 px-4`}
`;

const ErrorCard = styled.div`
    ${tw`max-w-md w-full bg-white rounded-lg shadow-xl p-8 text-center`}
`;

const ErrorIcon = styled.div`
    ${tw`text-6xl mb-4`}
`;

const ErrorTitle = styled.h1`
    ${tw`text-2xl font-bold text-gray-900 mb-2`}
`;

const ErrorMessage = styled.p`
    ${tw`text-gray-600 mb-6`}
`;

const ButtonGroup = styled.div`
    ${tw`flex flex-col gap-3`}
`;

const Button = styled.button`
    ${tw`px-6 py-3 rounded-lg font-semibold transition-all duration-300`}
    ${props => props.primary
        ? tw`bg-green-500 text-white hover:bg-green-600`
        : tw`bg-gray-200 text-gray-700 hover:bg-gray-300`
    }
`;

const ErrorDetails = styled.details`
    ${tw`mt-6 text-left text-sm`}
    summary {
        ${tw`cursor-pointer text-gray-500 hover:text-gray-700`}
    }
    pre {
        ${tw`mt-2 p-4 bg-gray-100 rounded overflow-auto text-xs`}
        max-height: 200px;
    }
`;

/**
 * Error Boundary Component
 * Ловит ошибки React и показывает пользователю понятное сообщение
 */
class ErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            hasError: false,
            error: null,
            errorInfo: null
        };
    }

    static getDerivedStateFromError(error) {
        return { hasError: true };
    }

    componentDidCatch(error, errorInfo) {
        console.error('🔴 Error Boundary caught error:', error, errorInfo);

        this.setState({
            error,
            errorInfo
        });

        // Логируем ошибку
        errorMonitoring.captureException(error, {
            componentStack: errorInfo.componentStack,
            boundary: this.props.fallbackName || 'ErrorBoundary'
        });
    }

    handleReload = () => {
        window.location.reload();
    };

    handleGoHome = () => {
        window.location.href = '/';
    };

    handleReset = () => {
        this.setState({
            hasError: false,
            error: null,
            errorInfo: null
        });
    };

    render() {
        if (this.state.hasError) {
            const { fallback } = this.props;

            // Если передан кастомный fallback
            if (fallback) {
                return fallback(this.state.error, this.handleReset);
            }

            // Дефолтный UI
            return (
                <ErrorContainer>
                    <ErrorCard>
                        <ErrorIcon>😔</ErrorIcon>
                        <ErrorTitle>Упс! Что-то пошло не так</ErrorTitle>
                        <ErrorMessage>
                            Произошла непредвиденная ошибка. Мы уже работаем над её исправлением.
                        </ErrorMessage>

                        <ButtonGroup>
                            <Button primary onClick={this.handleReload}>
                                Обновить страницу
                            </Button>
                            <Button onClick={this.handleGoHome}>
                                Вернуться на главную
                            </Button>
                        </ButtonGroup>

                        {process.env.NODE_ENV === 'development' && this.state.error && (
                            <ErrorDetails>
                                <summary>Детали ошибки (только для разработчиков)</summary>
                                <pre>
                                    <strong>Error:</strong> {this.state.error.toString()}
                                    {'\n\n'}
                                    <strong>Stack:</strong>
                                    {'\n'}
                                    {this.state.errorInfo?.componentStack}
                                </pre>
                            </ErrorDetails>
                        )}
                    </ErrorCard>
                </ErrorContainer>
            );
        }

        return this.props.children;
    }
}

export default ErrorBoundary;

/**
 * Специализированные Error Boundaries
 */

// Error Boundary для форм
export class FormErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false };
    }

    static getDerivedStateFromError() {
        return { hasError: true };
    }

    componentDidCatch(error, errorInfo) {
        errorMonitoring.captureException(error, {
            component: 'Form',
            ...errorInfo
        });
    }

    render() {
        if (this.state.hasError) {
            return (
                <div style={{ padding: '20px', background: '#fee', borderRadius: '8px', color: '#c00' }}>
                    <p><strong>Ошибка формы</strong></p>
                    <p>Не удалось загрузить форму. Попробуйте обновить страницу.</p>
                    <button
                        onClick={() => window.location.reload()}
                        style={{
                            marginTop: '10px',
                            padding: '8px 16px',
                            background: '#c00',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer'
                        }}
                    >
                        Обновить
                    </button>
                </div>
            );
        }

        return this.props.children;
    }
}

// Error Boundary для списков
export class ListErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false };
    }

    static getDerivedStateFromError() {
        return { hasError: true };
    }

    componentDidCatch(error, errorInfo) {
        errorMonitoring.captureException(error, {
            component: 'List',
            ...errorInfo
        });
    }

    render() {
        if (this.state.hasError) {
            return (
                <div style={{ padding: '40px', textAlign: 'center', color: '#666' }}>
                    <p>Не удалось загрузить список</p>
                    <button
                        onClick={() => this.setState({ hasError: false })}
                        style={{
                            marginTop: '10px',
                            padding: '8px 16px',
                            background: '#0ABD19',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer'
                        }}
                    >
                        Попробовать снова
                    </button>
                </div>
            );
        }

        return this.props.children;
    }
}